/**
 * @file ShopAccessTab.tsx
 * @description Вкладка «Доступ» карточки магазина (phase19.28 этап А;
 * uiux-platform-shops §4 «Доступ»; PL2, PL7, PL17). Видна только с правом
 * `PLATFORM_MANAGE_USERS` (маршрут).
 *
 * Владелец: имя и почта; приглашение ждёт — «Приглашение отправлено, не
 * принято» и «Отправить ещё раз» (прежняя ссылка гаснет, PL7). «Кабинет ↗» —
 * вход под владельцем с обязательной причиной и записью в журнал (PL17).
 * Смена владельца — этап Б, здесь её нет.
 */

import { LogIn, Mail, ShieldUser } from "lucide-react";
import { useState } from "react";
import { Link } from "react-router";

import { useHasPermission } from "../../../auth/hooks";
import { useShopCardContext } from "../../../hooks/platform/useShopCard";
import { IMPERSONATE_PERMISSION, platformShops, type OwnerInvitationResult, type ShopOwner } from "../../../lib/api-platform-shops";
import { CopyButton } from "../../../components/platform/shops/CopyButton";
import { apiErrorText } from "../../../lib/platform/shop-errors";
import { formatPlatformDate } from "../../../lib/platform/shop-format";
import { Button } from "../../../components/ui/button";
import { EmptyState } from "../../../components/ui/EmptyState";
import { NoticeStrip } from "../../../components/ui/NoticeStrip";
import { useNotify } from "../../../components/ui/NotifyProvider";
import { SectionCard } from "../../../components/ui/SectionCard";

function OwnerRow({ owner, onEnter }: { owner: ShopOwner; onEnter?: () => void }) {
  return (
    <li className="flex flex-col gap-2 py-3 sm:flex-row sm:items-center">
      <span className="flex min-w-0 flex-1 flex-col">
        <span className="type-body font-medium">{owner.displayName || owner.email}</span>
        <span className="type-caption break-all text-on-surface-variant">{owner.email} · Владелец магазина</span>
      </span>
      {onEnter ? (
        <Button variant="outline" onClick={onEnter} className="self-start sm:self-auto">
          <LogIn className="size-4" aria-hidden />
          Кабинет ↗
        </Button>
      ) : null}
    </li>
  );
}

export function ShopAccessTab() {
  const { shop, reload, enterCabinet } = useShopCardContext();
  const notify = useNotify();
  const canImpersonate = useHasPermission(IMPERSONATE_PERMISSION);
  const [sending, setSending] = useState(false);
  // Последняя отправка не дошла (delivered: false) — ссылку передают владельцу сами (QA D-B9).
  const [undelivered, setUndelivered] = useState<OwnerInvitationResult | null>(null);
  const invitation = shop.ownerInvitation;

  async function resend() {
    setSending(true);
    try {
      const sent = await platformShops.resendInvitation(shop.id);
      if (sent.delivered === false) {
        // Письмо не ушло — ссылку передают владельцу сами (PL7: прежняя уже погашена).
        setUndelivered(sent);
        notify.warning("Письмо не ушло — скопируйте ссылку и передайте владельцу");
      } else {
        setUndelivered(null);
        notify.success(`Приглашение отправлено ${sent.sentTo}, действует до ${formatPlatformDate(sent.expiresAt)}`);
      }
      reload();
    } catch (err) {
      notify.error(apiErrorText(err, "Письмо не отправлено: почтовый сервер не ответил. Повторите позже"));
    } finally {
      setSending(false);
    }
  }

  return (
    <div className="flex min-w-0 flex-col gap-4">
      <SectionCard title="Владелец">
        {shop.owners.length === 0 ? (
          <EmptyState icon={ShieldUser} title="Владельца нет" description="Назначить нового владельца можно будет на этапе Б." />
        ) : (
          <ul className="flex flex-col divide-y divide-[var(--border)]">
            {shop.owners.map((owner) => (
              <OwnerRow
                key={owner.id}
                owner={owner}
                onEnter={canImpersonate && invitation.status !== "pending" ? () => enterCabinet(owner.id) : undefined}
              />
            ))}
          </ul>
        )}
        {invitation.status === "pending" && undelivered ? (
          // Кнопка — под текстом (а не справа): на 360 текст не сжимается в столбец (QA D-N2).
          <NoticeStrip tone="error" className="mt-3">
            <p className="break-words">
              Письмо не ушло — скопируйте ссылку и передайте владельцу ({undelivered.sentTo}). Ссылка действует до{" "}
              {formatPlatformDate(undelivered.expiresAt)}.
            </p>
            {undelivered.url ? (
              <div className="mt-2">
                <CopyButton value={undelivered.url} label="Копировать ссылку" withText />
              </div>
            ) : null}
          </NoticeStrip>
        ) : null}
        {invitation.status === "pending" ? (
          <NoticeStrip tone="warning" className="mt-3">
            <p className="break-words">
              {undelivered ? "Приглашение не принято" : `Приглашение отправлено${invitation.sentTo ? ` ${invitation.sentTo}` : ""}, не принято`}
              {invitation.expiresAt ? ` · действует до ${formatPlatformDate(invitation.expiresAt)}` : ""}. Повторная отправка
              гасит прежнюю ссылку.
            </p>
            <div className="mt-2">
              <Button variant="soft" onClick={() => void resend()} loading={sending}>
                <Mail className="size-4" aria-hidden />
                Отправить ещё раз
              </Button>
            </div>
          </NoticeStrip>
        ) : null}
      </SectionCard>
      <p className="type-body text-on-surface-variant">
        Сотрудники магазина и их роли —{" "}
        <Link to="/platform/users" className="inline-flex min-h-12 items-center font-medium text-primary underline-offset-4 hover:underline">
          в разделе «Пользователи» →
        </Link>
      </p>
    </div>
  );
}
